import {FC} from 'react'
import {useIntl} from 'react-intl'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {IOfferSummary} from '../cors/_models'
import SharedData from '../../common/SharedData'
import {isShopUser} from '../../common/stockScope'

/**
 * Top-of-page strip: at-a-glance counts so the supplier sees the health
 * of their offer book before scrolling. The page loads the counts (it also
 * needs them for the filter) and reloads them after a mutation.
 */

type IProps = {
  data: IOfferSummary | null
}

type Tone = 'default' | 'success' | 'tertiary'

const tile = (
  label: string,
  value: number,
  opts: {tone?: Tone; dot?: boolean; tip?: string} = {},
) => {
  const {tone = 'default', dot = false, tip} = opts
  return (
    <div className={`hsb-of-stat ${tone !== 'default' ? `hsb-of-stat--${tone}` : ''}`}>
      <p className="hsb-of-stat-label">
        {label}
        {tip ? (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`summary-tip-${label}`}>{tip}</Tooltip>}
          >
            <i className="fas fa-circle-info hsb-of-tip" aria-label={tip} />
          </OverlayTrigger>
        ) : null}
      </p>
      <div className="hsb-of-stat-row">
        <p className="hsb-of-stat-value">{value}</p>
        {dot ? <span className={`hsb-of-dot hsb-of-dot--${tone === 'tertiary' ? 'tertiary' : 'success'}`} /> : null}
      </div>
    </div>
  )
}

const SummaryStrip: FC<IProps> = ({data}) => {
  const {formatMessage} = useIntl()
  const {currentUser} = SharedData()

  if (!data) return null

  return (
    <div
      className="hsb-of-stats"
      role="region"
      aria-label={formatMessage({id: 'offers.summary.aria'})}
    >
      <div className="hsb-of-stats-grid">
        {tile(formatMessage({id: 'offers.summary.total'}), data.total_offers)}
        {tile(formatMessage({id: 'offers.summary.active'}), data.active_offers, {tone: 'success', dot: true})}
        {tile(formatMessage({id: 'offers.summary.winning'}), data.winning_offers, {tone: 'success', dot: true})}
        {tile(formatMessage({id: 'offers.summary.low_stock'}), data.low_stock_offers, {
          tone: 'tertiary',
          dot: true,
          tip: formatMessage({id: 'offers.summary.low_stock_tip'}),
        })}
        {tile(formatMessage({id: 'offers.summary.out_of_stock'}), data.out_of_stock_offers)}
        {/* The portal refuses a zero price, so this is normally empty — only
            shown when a feed has left an offer without one. */}
        {data.unpriced_offers > 0
          ? tile(formatMessage({id: 'offers.summary.unpriced'}), data.unpriced_offers)
          : null}
        {/* A partner shop's stock is its shelf, not Dumyah's FC: its tile counts shelf units. */}
        {isShopUser(currentUser)
          ? tile(formatMessage({id: 'shop_scope.stock_total_on_shelf'}), data.total_units)
          : tile(formatMessage({id: 'offers.summary.warehouse_units'}), data.warehouse_units)}
      </div>
    </div>
  )
}

export default SummaryStrip
